import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { VideoCamera, GoogleLogo, Plus, Calendar, ArrowSquareOut, X, CheckCircle } from "@phosphor-icons/react";

function fmtDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function TutoringPage() {
  const { user } = useAuth();
  const [connected, setConnected] = useState(null);
  const [googleEmail, setGoogleEmail] = useState("");
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", title: "Sessão de tutoria 1:1", description: "", date: "", time: "", duration_min: 45 });
  const [creating, setCreating] = useState(false);

  async function load() {
    const s = await api.get("/tutoring/sessions"); setSessions(s.data);
    if (user?.role === "admin" || user?.role === "teacher") {
      const [st, gs] = await Promise.all([api.get("/students"), api.get("/oauth/google/status")]);
      setStudents(st.data); setConnected(gs.data.connected); setGoogleEmail(gs.data.email || "");
    }
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("connected") === "1") {
      toast.success("Google conectado!");
      url.searchParams.delete("connected");
      window.history.replaceState({}, "", url.toString());
      load();
    }
    // eslint-disable-next-line
  }, []);

  async function connectGoogle() {
    const { data } = await api.get("/oauth/google/login");
    window.location.href = data.authorization_url;
  }
  async function disconnect() {
    await api.post("/oauth/google/disconnect"); toast.success("Google desconectado"); load();
  }

  async function create() {
    if (!connected) {
      toast.error("Conecte seu Google primeiro para criar a videoconferência.");
      return;
    }
    if (!form.student_id || !form.date || !form.time) { toast.error("Selecione aluno, data e horário"); return; }
    setCreating(true);
    try {
      const startISO = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.post("/tutoring/sessions", {
        student_id: form.student_id, title: form.title, description: form.description,
        start: startISO, duration_min: form.duration_min,
      });
      toast.success("Sessão agendada e evento criado no Google Calendar!");
      setOpen(false); setForm({ student_id: "", title: "Sessão de tutoria 1:1", description: "", date: "", time: "", duration_min: 45 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao agendar"); }
    finally { setCreating(false); }
  }

  function tryOpen(next) {
    if (next && !connected) {
      toast.error("Conecte seu Google primeiro para criar a videoconferência.");
      return;
    }
    setOpen(next);
  }

  async function cancel(id) {
    if (!confirm("Cancelar esta sessão? O evento também será removido do Calendar.")) return;
    await api.delete(`/tutoring/sessions/${id}`); toast.success("Sessão cancelada"); load();
  }

  const active = sessions.filter(s => s.status !== "cancelled");

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl space-y-6 sm:space-y-8">
      <header className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
            <VideoCamera size={12} weight="fill" /> Sessões 1:1
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">Tutoria & Meet</h1>
          <p className="text-zinc-500 mt-2 text-sm sm:text-base">Reuniões individuais aluno-professor sincronizadas com o Google Calendar.</p>
        </div>
        {(user?.role === "admin" || user?.role === "teacher") && (
          <Dialog open={open} onOpenChange={tryOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-session-btn"
                      className="btn-pill h-12 gradient-primary hover:opacity-90 text-white shadow-[0_8px_24px_rgba(30,95,255,0.35)] hover:-translate-y-0.5">
                <VideoCamera size={16} className="mr-2" weight="fill" /> Nova videoconferência
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle className="font-heading text-2xl font-black">Nova sessão de tutoria</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Aluno</Label>
                  <Select value={form.student_id} onValueChange={v => setForm({...form, student_id: v})}>
                    <SelectTrigger data-testid="ts-student" className="mt-2 h-12 rounded-2xl"><SelectValue placeholder="Selecione"/></SelectTrigger>
                    <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} — {s.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Título</Label><Input data-testid="ts-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
                <div><Label>Descrição / pauta</Label><Textarea data-testid="ts-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-2 rounded-2xl" rows={3}/></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Data</Label><Input data-testid="ts-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
                  <div><Label>Horário</Label><Input data-testid="ts-time" type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
                  <div><Label>Duração (min)</Label><Input type="number" value={form.duration_min} onChange={e => setForm({...form, duration_min: parseInt(e.target.value) || 45})} className="mt-2 h-12 rounded-2xl"/></div>
                </div>
                <Button data-testid="ts-submit" onClick={create} disabled={creating} className="w-full h-12 btn-pill gradient-primary hover:opacity-90 text-white">
                  {creating ? "Agendando..." : "Agendar e criar Meet"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </header>

      {(user?.role === "admin" || user?.role === "teacher") && (
        <div className={`card-soft p-4 sm:p-6 fade-up flex flex-col sm:flex-row items-start sm:items-center gap-4 ${connected ? "border-[#12B76A]/40 bg-[#EAF7EF]/30" : "border-[#1E5FFF]/30 bg-[#E0EBFF]/30"}`}>
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${connected ? "bg-[#EAF7EF]" : "bg-white"}`}>
            {connected ? <CheckCircle size={26} weight="duotone" color="#12B76A" /> : <GoogleLogo size={26} weight="bold" color="#1E5FFF" />}
          </div>
          <div className="flex-1 min-w-0">
            {connected ? (
              <>
                <div className="font-heading font-black text-lg">Google conectado</div>
                <div className="text-sm text-zinc-600 truncate">Eventos na agenda de <b>{googleEmail}</b></div>
              </>
            ) : (
              <>
                <div className="font-heading font-black text-lg">Conecte seu Google</div>
                <div className="text-sm text-zinc-600">Para criar eventos e gerar links do Meet automaticamente.</div>
              </>
            )}
          </div>
          {connected ? (
            <Button variant="outline" data-testid="google-disconnect" onClick={disconnect} className="w-full sm:w-auto rounded-full">Desconectar</Button>
          ) : (
            <Button data-testid="google-connect" onClick={connectGoogle}
                    className="btn-pill h-11 w-full sm:w-auto bg-white border border-zinc-200 text-[#12143A] hover:bg-zinc-50">
              <GoogleLogo size={16} className="mr-2" weight="bold" /> Conectar com Google
            </Button>
          )}
        </div>
      )}

      <div className="fade-up">
        <h2 className="font-heading text-2xl font-black mb-4 flex items-center gap-2">
          <Calendar size={22} weight="duotone" color="#1E5FFF" /> Próximas sessões
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {active.length === 0 && (
            <div className="col-span-full text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-2xl dot-grid">
              Nenhuma sessão agendada.
            </div>
          )}
          {active.map(s => (
            <div key={s.id} data-testid={`session-${s.id}`} className="card-soft p-6">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0 shadow-md">
                  <VideoCamera size={22} color="white" weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-black text-lg leading-tight">{s.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">com {s.student_email}</div>
                </div>
                {(user?.role === "admin" || user?.role === "teacher") && (
                  <button onClick={() => cancel(s.id)} className="text-zinc-400 hover:text-[#FF3D71]" title="Cancelar"><X size={16} weight="bold" /></button>
                )}
              </div>
              {s.description && <p className="text-sm text-zinc-600 mt-3">{s.description}</p>}
              <div className="mt-4 flex items-center gap-3 text-xs text-zinc-600">
                <span className="pill-badge bg-[#E0EBFF] text-[#1E5FFF]">{fmtDT(s.start)}</span>
                <span className="font-mono-num">{s.duration_min} min</span>
              </div>
              <div className="mt-4 flex gap-2">
                {s.meet_link && (
                  <a href={s.meet_link} target="_blank" rel="noreferrer"
                     data-testid={`meet-${s.id}`}
                     className="flex-1 h-11 rounded-full flex items-center justify-center gap-2 gradient-primary text-white font-bold text-sm shadow-[0_6px_18px_rgba(30,95,255,0.35)] hover:-translate-y-0.5 transition">
                    <VideoCamera size={16} weight="fill" /> Entrar no Meet
                  </a>
                )}
                {s.html_link && (
                  <a href={s.html_link} target="_blank" rel="noreferrer"
                     className="h-11 px-4 rounded-full flex items-center justify-center gap-1 bg-white border border-zinc-200 text-zinc-700 font-semibold text-sm hover:bg-zinc-50">
                    <ArrowSquareOut size={14} /> Calendar
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
